import { useState } from 'react';
import { Link } from 'react-router-dom';
import './AboutPage.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import underlineImg from '../assets/underline.png';
import { IconLightning } from '../components/Icons';

const SERVICES = [
  {
    name: 'Graphic Design',
    accentColor: '#7c3aed',
    gradient: 'linear-gradient(135deg,#7c3aed,#4f46e5)',
    shadow: 'rgba(124,58,237,0.28)',
    image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=640&q=80&fit=crop',
    imageAlt: 'Graphic designer creating brand identity, packaging design and marketing creatives in Pune',
    cta: { label: 'Get Started', href: '/#contact' },
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/>
        <path d="M2 2l7.586 7.586"/><circle cx="11" cy="11" r="2"/>
      </svg>
    ),
    description: 'From brand identity systems to high-converting ad creatives, our design team builds visuals that make your brand unforgettable — across digital, print, e-commerce packaging, and Amazon A+ Content.',
    points: ['Brand Identity & Logos', 'Social Media Creatives', 'Packaging & Print Design', 'Amazon A+ Content', 'Performance Ad Banners', 'Canva Brand Kits'],
    details: [
      'Logo design — primary, secondary & icon-only mark variants with full usage guide',
      'Full brand guidelines: colour palettes, typography, spacing, tone & usage rules',
      'Social media creative templates for Instagram, Facebook, YouTube & LinkedIn',
      'Print-ready business cards, letterheads, brochures, catalogues & product flyers',
      'Product packaging design with 3D mockups, structural dielines & print-ready files',
      'Amazon A+ Content design, product infographic images & Brand Storefront layouts',
      'Google Display ads, remarketing banners & performance marketing creatives',
      'Infographic design & data visualisation for blogs, emails & presentations',
      'Canva brand kit setup — locked, editable templates for your in-house team',
      'Carousel ad creatives, Instagram story frames & YouTube thumbnail design',
    ],
  },
  {
    name: 'Videography · Pune',
    accentColor: '#ea580c',
    gradient: 'linear-gradient(135deg,#ea580c,#f59e0b)',
    shadow: 'rgba(234,88,12,0.28)',
    image: 'https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=640&q=80&fit=crop',
    imageAlt: 'Professional videography and product video production studio in Pune India',
    cta: { label: 'Book a Shoot', href: '/#contact' },
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15 10l4.553-2.276A1 1 0 0 1 21 8.723v6.554a1 1 0 0 1-1.447.894L15 14v-4z"/>
        <rect x="3" y="8" width="12" height="8" rx="2"/>
      </svg>
    ),
    description: 'Studio-quality video and photo production right here in Pune. We capture your brand\'s story through cinematic brand films, product shoots, aerial drone footage, and scroll-stopping content built to convert and perform.',
    points: ['Product Photography & Shoots', 'Brand Films & Reels', 'Drone & Aerial Footage', 'Testimonial Videos', 'Corporate Event Coverage', '48-Hour Rush Packages'],
    details: [
      'In-studio & on-location product photography — tabletop, lifestyle & flat-lay',
      'Brand story films (60s–3 min) for website hero sections, ads & investor decks',
      'Short-form Reels, product ads & UGC-style video content (15s–60s)',
      'Customer testimonial & case study video production with b-roll & supers',
      'Corporate event, conference, office walkthrough & product launch videography',
      'Aerial drone footage for location shoots, lifestyle & product reveal videos (Pune & nearby)',
      'White-label & influencer-style content production for D2C e-commerce brands',
      'Full post-production included — colour grading, sound design, subtitles & delivery',
      'Equipment: Sony FX3 · DJI Drone · Aputure Lighting · DJI Ronin Gimbal',
      'Same-day or 48-hour turnaround rush packages available on request',
    ],
  },
  {
    name: 'E-commerce Marketing',
    accentColor: '#2563eb',
    gradient: 'linear-gradient(135deg,#2563eb,#0891b2)',
    shadow: 'rgba(37,99,235,0.28)',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=640&q=80&fit=crop',
    imageAlt: 'E-commerce marketing analytics and performance ad campaign management dashboard',
    cta: { label: 'View Marketplace Services', href: '/services/amazon' },
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/>
        <polyline points="16 7 22 7 22 13"/>
      </svg>
    ),
    description: 'Data-driven campaigns across Amazon, Flipkart, Meta, Google and beyond. We manage your ad spend, optimise for ROAS, and build scalable growth strategies that drive predictable revenue across every major marketplace and platform.',
    points: ['Meta & Google Performance Ads', 'Amazon & Flipkart PPC', 'Marketplace Account Management', 'Email & WhatsApp Marketing', 'SEO & Content Strategy', 'International Expansion'],
    details: [
      'Meta (Facebook & Instagram) performance ad campaigns, retargeting & lookalikes',
      'Google Search, Shopping & Display ad management with conversion tracking',
      'Amazon Sponsored Products, Sponsored Brands & Sponsored Display PPC',
      'Flipkart, Meesho & Myntra ad setup, optimisation & budget management',
      'Email automation flows, drip sequences & WhatsApp broadcast campaigns',
      'E-commerce SEO audits, keyword research & content strategy',
      'Amazon & Flipkart account management — cataloguing, A+ Content & Brand Store',
      'Quick Commerce ads — Blinkit, Zepto & Swiggy Instamart campaign management',
      'International marketplace expansion: Noon, Walmart, eBay & AliExpress',
      'Full-funnel ROAS reporting, multi-touch attribution & monthly performance reviews',
    ],
  },
  {
    name: 'UGC Content',
    accentColor: '#db2777',
    gradient: 'linear-gradient(135deg,#db2777,#9333ea)',
    shadow: 'rgba(219,39,119,0.28)',
    image: 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=640&q=80&fit=crop',
    imageAlt: 'Content creator filming UGC user generated content video for social media ads',
    cta: { label: 'Get Started', href: '/#contact' },
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    description: 'Authentic creator-driven content that consistently outperforms branded ads. We source, brief, and produce UGC assets specifically built for high-converting ad campaigns on Meta, TikTok, YouTube, and Amazon.',
    points: ['UGC Video Creation', 'Creator Sourcing & Briefing', 'Ad-Ready UGC Assets', 'Hook Testing & Iteration', 'Bulk Content Packages', 'Testimonial-Style Ads'],
    details: [
      'Sourcing & vetting creators matched to your brand niche & target demographic',
      'Detailed creative briefs with hooks, CTAs, scripts & visual mood references',
      'UGC unboxing, review, tutorial & product demonstration videos',
      'Testimonial-style content optimised for Meta & TikTok paid ad performance',
      'Bulk content production packages — 10, 25, 50+ assets per batch on retainer',
      'Hook testing & creative iteration based on real ad performance data',
      'Amazon Vine-style review video content for product listing optimisation',
      'Trending-audio native-format Reels for organic reach & algorithm growth',
      'Creator contracts, usage rights management & content licensing',
      'End-to-end delivery — from brief to ad-ready asset in under 7 days',
    ],
  },
  {
    name: 'Video & Photo Editing',
    accentColor: '#0d9488',
    gradient: 'linear-gradient(135deg,#0d9488,#16a34a)',
    shadow: 'rgba(13,148,136,0.28)',
    image: 'https://images.unsplash.com/photo-1626785774625-ddcddc3445e9?w=640&q=80&fit=crop',
    imageAlt: 'Professional video editing and post-production setup using Adobe Premiere Pro and DaVinci Resolve',
    cta: { label: 'Get Started', href: '/#contact' },
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="6" cy="6" r="3"/><circle cx="18" cy="18" r="3"/>
        <line x1="8.46" y1="8.46" x2="15.54" y2="15.54"/>
        <line x1="20.54" y1="3.46" x2="15.54" y2="8.46"/>
        <line x1="3.46" y1="20.54" x2="8.46" y2="15.54"/>
      </svg>
    ),
    description: 'Turn raw footage and images into polished, scroll-stopping content. Our editors deliver fast-turnaround Reels, product retouching, e-commerce catalogue editing, and ad creatives at scale using industry-standard tools.',
    points: ['Reels & Short-Form Editing', 'Product Photo Retouching', 'Colour Grading & VFX', 'Batch Catalogue Editing', 'Motion Graphics', 'Podcast Video Editing'],
    details: [
      'Instagram Reels, YouTube Shorts & TikTok editing (15s–90s) with captions & transitions',
      'Long-form YouTube video editing — chapters, B-roll, captions & branded intros/outros',
      'Product demo, explainer, how-to & tutorial video post-production',
      'E-commerce catalogue video editing — batch orders accepted on monthly retainer',
      'Cinematic colour grading with custom LUTs using DaVinci Resolve',
      'Motion graphics, kinetic typography & lower-third animations (After Effects)',
      'Podcast video editing — B-roll cutaways, dynamic captions & branded layouts',
      'Raw photo culling, skin retouching & background removal (Photoshop / Lightroom)',
      'Batch marketplace image editing — white-background, infographic & lifestyle (Amazon, Flipkart)',
      'Tools: Adobe Premiere Pro · After Effects · DaVinci Resolve · CapCut · Lightroom',
    ],
  },
  {
    name: 'Web Development',
    accentColor: '#4338ca',
    gradient: 'linear-gradient(135deg,#4338ca,#2563eb)',
    shadow: 'rgba(67,56,202,0.28)',
    image: 'https://images.unsplash.com/photo-1547658719-da2b51169166?w=640&q=80&fit=crop',
    imageAlt: 'Web developer building high-performance Shopify e-commerce store and React website',
    cta: { label: 'Start Your Project', href: '/#contact' },
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6"/>
        <polyline points="8 6 2 12 8 18"/>
      </svg>
    ),
    description: 'High-performance websites and e-commerce stores built for speed, SEO, and conversions. From Shopify D2C stores and custom React apps to WordPress sites, corporate portals, and high-converting landing pages — we build digital experiences that drive real results.',
    points: ['Shopify & Shopify Plus', 'React / Next.js Apps', 'WordPress & WooCommerce', 'Corporate & Portfolio Sites', 'Landing Pages & Funnels', 'CRO & Speed Optimisation'],
    details: [
      'Shopify store setup, custom Liquid theme development & third-party app integrations',
      'Shopify Plus — B2B portals, multi-store architecture & custom checkout flows',
      'WooCommerce / WordPress websites for e-commerce, service businesses & blogs',
      'Custom React / Next.js web applications — blazing-fast, SEO-ready & fully scalable',
      'Company profile, agency portfolio & startup websites with headless CMS',
      'Service booking portals, appointment scheduling systems & custom client dashboards',
      'High-converting product launch & lead generation landing pages',
      'Sales funnel pages — VSL pages, webinar registrations & squeeze pages',
      'Core Web Vitals optimisation: 90+ PageSpeed scores, LCP & CLS fixes',
      'Mobile-first UI/UX design, A/B testing setup & conversion rate optimisation (CRO)',
    ],
  },
];

const GALLERY_IMAGES = [
  {
    // land — wide landscape: cinematic video production
    src: 'https://images.unsplash.com/photo-1598899134739-24c46f58b8c0?w=700&h=350&q=85&fit=crop',
    alt: 'Cinematic video production and brand film shoot for e-commerce brand',
  },
  {
    // port — tall portrait: photographer with camera
    src: 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=400&h=700&q=85&fit=crop',
    alt: 'Professional photographer capturing brand content and product imagery',
  },
  {
    // sq1 — square: colorful graphic design work
    src: 'https://images.unsplash.com/photo-1626785774625-ddcddc3445e9?w=500&h=500&q=85&fit=crop',
    alt: 'Graphic designer creating brand identity and visual assets',
  },
  {
    // sq2 — square: product flat lay shoot
    src: 'https://images.unsplash.com/photo-1560343090-f0409e92791a?w=500&h=500&q=85&fit=crop',
    alt: 'Product flat lay photography for e-commerce marketplace listing',
  },
  {
    // wide — panoramic strip: team brainstorming
    src: 'https://images.unsplash.com/photo-1542744094-24638eff58bb?w=900&h=350&q=85&fit=crop',
    alt: 'Creative team collaborating on digital marketing strategy and campaigns',
  },
  {
    // rect — wide landscape: e-commerce analytics
    src: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=700&h=350&q=85&fit=crop',
    alt: 'E-commerce analytics and performance marketing dashboard',
  },
  {
    // sq3 — square: video editing post-production
    src: 'https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=500&h=500&q=85&fit=crop',
    alt: 'Professional video editing and post-production for brand content',
  },
];

const STATS = [
  {
    num: '20+', label: 'Brands Scaled',
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>,
  },
  {
    num: '98%', label: 'Client Satisfaction',
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>,
  },
  {
    num: '10+', label: 'Years Combined Exp.',
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg>,
  },
  {
    num: '24/7', label: 'Support Available',
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.06 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 16.92z"/></svg>,
  },
];

const ANDAAZ_SERVICES = [
  {
    label: 'Brand Films & Reels',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 10l4.553-2.276A1 1 0 0 1 21 8.723v6.554a1 1 0 0 1-1.447.894L15 14v-4z"/><rect x="3" y="8" width="12" height="8" rx="2"/></svg>,
  },
  {
    label: 'Product Photography',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>,
  },
  {
    label: 'Graphic & Motion Design',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="13.5" cy="6.5" r=".5"/><circle cx="17.5" cy="10.5" r=".5"/><circle cx="8.5" cy="7.5" r=".5"/><circle cx="6.5" cy="12.5" r=".5"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/></svg>,
  },
  {
    label: 'UGC & Social Content',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>,
  },
  {
    label: 'Video & Photo Editing',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6" r="3"/><circle cx="18" cy="18" r="3"/><line x1="8.46" y1="8.46" x2="15.54" y2="15.54"/><line x1="20.54" y1="3.46" x2="15.54" y2="8.46"/><line x1="3.46" y1="20.54" x2="8.46" y2="15.54"/></svg>,
  },
];

export function CreativeSections() {
  const [activeIdx, setActiveIdx] = useState(0);
  const activeSvc = SERVICES[activeIdx];

  return (
    <>
      {/* ── SECTION 1: Creative Services ── */}
      <section className="ap-services">
        <div className="ap-services-inner">

          <div className="ap-svc-header">
            <div className="badge ap-badge">
              <span className="badge-icon"><IconLightning /></span>
              360° CREATIVE SERVICES
            </div>
            <h1 className="ap-svc-title">
              All Creative Services
              <span className="ap-svc-gradient"> Under One Roof</span>
            </h1>
            <img src={underlineImg} alt="" className="ap-underline" />
            <p className="ap-svc-sub">
              We craft powerful creative experiences that help e-commerce brands grow, engage, and scale.
            </p>
          </div>

          {/* ── Explorer: left nav + right detail panel ── */}
          <div className="ap-svc-explorer">

            {/* Left — compact service nav */}
            <nav className="ap-svc-nav" aria-label="Services">
              {SERVICES.map((svc, idx) => (
                <button
                  key={svc.name}
                  className={`ap-svc-nav-btn${activeIdx === idx ? ' active' : ''}`}
                  onClick={() => setActiveIdx(idx)}
                  style={activeIdx === idx ? {
                    borderLeftColor: svc.accentColor,
                    background: `${svc.accentColor}0d`,
                  } : {}}
                >
                  <span
                    className="ap-svc-nav-icon"
                    style={{
                      background: svc.gradient,
                      boxShadow: activeIdx === idx ? `0 4px 14px ${svc.shadow}` : 'none',
                    }}
                  >
                    {svc.icon}
                  </span>
                  <span className="ap-svc-nav-label" style={activeIdx === idx ? { color: svc.accentColor } : {}}>
                    {svc.name}
                  </span>
                  <svg
                    className={`ap-svc-nav-chevron${activeIdx === idx ? ' rotated' : ''}`}
                    width="14" height="14" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round"
                  >
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
              ))}
            </nav>

            {/* Right — detail panel */}
            <div className="ap-svc-detail-panel">
              <div className="ap-svc-detail-content" key={activeIdx}>
                {/* Hero header */}
                <div className="ap-svc-detail-hero" style={{ background: activeSvc.gradient }}>
                  <div className="ap-svc-detail-hero-icon">
                    {activeSvc.icon}
                  </div>
                  <div>
                    <h3 className="ap-svc-detail-name">{activeSvc.name}</h3>
                    <p className="ap-svc-detail-tag">Creative Service</p>
                  </div>
                </div>

                {/* Body */}
                <div className="ap-svc-detail-body">
                  {/* Service image */}
                  <img
                    src={activeSvc.image}
                    alt={activeSvc.imageAlt}
                    className="ap-svc-detail-img"
                    loading="lazy"
                  />

                  <p className="ap-svc-detail-desc">{activeSvc.description}</p>

                  <div className="ap-svc-detail-included">
                    <span className="ap-svc-detail-included-label">What's included</span>
                    <ul className="ap-svc-detail-list">
                      {activeSvc.details.map((d, i) => (
                        <li key={i} className="ap-svc-detail-li">
                          <span className="ap-svc-detail-check" style={{ background: `${activeSvc.accentColor}12`, borderColor: `${activeSvc.accentColor}30`, color: activeSvc.accentColor }}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          </span>
                          {d}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {activeSvc.name === 'E-commerce Marketing' ? (
                    <Link to={activeSvc.cta.href} className="ap-svc-detail-cta">
                      {activeSvc.cta.label}
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12"/>
                        <polyline points="12 5 19 12 12 19"/>
                      </svg>
                    </Link>
                  ) : (
                    <a href={activeSvc.cta.href} className="ap-svc-detail-cta">
                      {activeSvc.cta.label}
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12"/>
                        <polyline points="12 5 19 12 12 19"/>
                      </svg>
                    </a>
                  )}
                </div>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* ── SECTION 2: Mad Andaaz ── */}
      <section className="ap-andaaz">
        <div className="ap-andaaz-inner">

          {/* Header */}
          <div className="ap-andaaz-header">
            <div className="badge ap-andaaz-badge">
              <span className="badge-icon"><IconLightning /></span>
              MEET MAD ANDAAZ
            </div>
            <h2 className="ap-andaaz-title">
              The Creative Arm
              <span className="ap-andaaz-gradient"> of MMC</span>
            </h2>
            <img src={underlineImg} alt="" className="ap-underline" />
            <p className="ap-andaaz-sub">
              Mad Andaaz is where strategy meets storytelling — Pune's creative studio built for e-commerce brands.
            </p>
          </div>

          {/* Split: left text — right photo collage */}
          <div className="ap-andaaz-split">

            {/* Left */}
            <div className="ap-andaaz-left">
              <p className="ap-andaaz-desc">
                While MMC drives e-commerce growth, Mad Andaaz brings brands to life through bold visuals,
                scroll-stopping content, and cinematic production that converts. From Pune-based product shoots
                to viral UGC campaigns — Mad Andaaz is where your brand's visual identity is crafted and amplified.
              </p>

              <div className="ap-andaaz-card">
                <div className="ap-andaaz-card-head">
                  <div className="ap-andaaz-avatar">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="2" width="20" height="20" rx="5"/>
                      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                    </svg>
                  </div>
                  <div>
                    <div className="ap-andaaz-handle">@madandaaz</div>
                    <div className="ap-andaaz-handle-sub">Creative Studio · Pune, India</div>
                  </div>
                </div>

                <div className="ap-andaaz-svc-list">
                  {ANDAAZ_SERVICES.map((item) => (
                    <div key={item.label} className="ap-andaaz-svc-row">
                      <span className="ap-andaaz-svc-icon">{item.icon}</span>
                      <span className="ap-andaaz-svc-text">{item.label}</span>
                      <span className="ap-andaaz-svc-tick">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <a
                href="https://www.instagram.com/madandaaz/"
                target="_blank"
                rel="noopener noreferrer"
                className="ap-andaaz-btn"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="20" rx="5"/>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                </svg>
                Follow @madandaaz
              </a>
            </div>

            {/* Right — asymmetric photo collage */}
            <div className="ap-andaaz-right">
              <div className="ap-gallery">
                {GALLERY_IMAGES.map((img, i) => (
                  <img
                    key={i}
                    src={img.src}
                    alt={img.alt}
                    className="ap-gallery-img"
                    loading="lazy"
                  />
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

    </>
  );
}

export default function AboutPage() {
  return (
    <>
      <Header />
      <CreativeSections />
      <Footer />
    </>
  );
}
